import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap, withLatestFrom } from 'rxjs/operators';
import { PipelineEnvironment, StepTask } from '@telespot/domain';
import { PipelineIntegrityService, TaskAvailability, TaskAvailabilityContext } from '@protocols/domain';

import { PipelineStepEditorState, TaskSelectionSnapshot } from '../state/pipeline-step-editor.state';

export interface AvailableTask {
  task: StepTask;
  disabled: boolean;
  reasonKey?: string;
}

const TASK_ORDER: StepTask[] = [
  StepTask.TEXT,
  StepTask.CLASSIFICATION,
  StepTask.ROIDETECTION,
  StepTask.SEGMENTATION,
  StepTask.POSITION,
  StepTask.OCR,
  StepTask.ROITRACKING,
  StepTask.MAPPING,
];

const REASON_KEYS = {
  environment: 'pipeline_step_editor.task.disabled_reason.environment',
  alreadyUsed: 'pipeline_step_editor.task.disabled_reason.already_used',
  orderZero: 'pipeline_step_editor.task.disabled_reason.order_zero',
  incompatibleWithCategories: 'pipeline_step_editor.task.disabled_reason.incompatible_with_categories',
} as const;

@Injectable()
export class PipelineTaskSelectionEffects {
  constructor(private readonly state: PipelineStepEditorState) {}

  public readonly availableTasks$: Observable<AvailableTask[]> = this.state.select(
    this.state.environment$,
    this.state.order$,
    this.state.otherStepsTasks$,
    this.state.task$,
    this.state.categories$,
    (env, order, otherTasks, currentTask, categories) => {
      const ctx: TaskAvailabilityContext = {
        env,
        order,
        otherStepsTasks: otherTasks,
        currentTask,
        hasCategories: categories.length > 0,
      };
      return TASK_ORDER.map((task) =>
        this.toAvailableTask(task, PipelineIntegrityService.evaluateTaskAvailability(task, ctx))
      );
    }
  );

  public readonly selectTask = this.state.effect((task$: Observable<StepTask>) =>
    task$.pipe(
      withLatestFrom(this.state.taskSelectionSnapshot$),
      tap(([task, snapshot]: [StepTask, TaskSelectionSnapshot]) => {
        const ctx: TaskAvailabilityContext = {
          env: snapshot.context?.environment ?? null,
          order: snapshot.context?.order ?? null,
          otherStepsTasks: snapshot.context?.otherStepsTasks ?? [],
          currentTask: snapshot.task,
          hasCategories: snapshot.categoriesCount > 0,
        };
        const availability = PipelineIntegrityService.evaluateTaskAvailability(task, ctx);
        if (availability.kind !== 'allowed' || snapshot.task === task) return;
        this.state.setTask(task);

        const keepCategories =
          snapshot.task !== null && PipelineIntegrityService.taskTransitionPreservesCategories(snapshot.task, task);
        if (keepCategories) {
          this.state.resetTaskScopedFlags();
        } else {
          this.state.resetTaskScopedFields();
        }

        if (snapshot.context?.environment === PipelineEnvironment.CLOUD) {
          this.state.setGenerateFinding(null);
          if (snapshot.cloudSubject === null) {
            this.state.setCloudSubject(PipelineIntegrityService.defaultCloudSubject());
          }
        } else if (snapshot.context?.environment === PipelineEnvironment.EDGE) {
          this.state.setCloudSubject(null);
          if (snapshot.generateFinding === null) {
            this.state.setGenerateFinding(PipelineIntegrityService.defaultGenerateFinding());
          }
        }
      })
    )
  );

  private toAvailableTask(task: StepTask, availability: TaskAvailability): AvailableTask {
    switch (availability.kind) {
      case 'allowed':
        return { task, disabled: false };
      case 'environment':
        return { task, disabled: true, reasonKey: REASON_KEYS.environment };
      case 'order_zero':
        return { task, disabled: true, reasonKey: REASON_KEYS.orderZero };
      case 'already_used':
        return { task, disabled: true, reasonKey: REASON_KEYS.alreadyUsed };
      case 'incompatible_with_categories':
        return { task, disabled: true, reasonKey: REASON_KEYS.incompatibleWithCategories };
    }
  }
}
